import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { MapProps } from './map-types';
import { MapContext } from './map-ctx';
import { useStyles } from './map-styles';

import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useMap, CrzpMap } from './hook/map-hook';
import { MapSuggest } from './map-suggest';

const defaultWgs84CenterPoint = { lat: 50.0755, lon: 14.4378 };
// const defaultJtskCenterPoint = { x: 1044493.079, y: 741818.445 };

export function MapContainer({
  width = '100%',
  height = 'calc(100vh - 353px)',
  defaultZoom = 7,
  defaultWgs84Position = defaultWgs84CenterPoint,
  mode = 'PREVIEW',
  onPointAdded,
  children,
  showSuggest = false,
}: MapProps & { showSuggest?: boolean }) {
  const classes = useStyles();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const [actualMapInstance, setActualMapInstance] = useState<CrzpMap | null>(
    null
  );

  const handleMapReady = useCallback((mapInstance: CrzpMap | null) => {
    setActualMapInstance(mapInstance);
  }, []);

  const mapOptions = useMemo(
    () => ({
      defaultWgs84Position,
      defaultZoom,
      //mode, // avoid rerending map
    }),
    [defaultWgs84Position, defaultZoom]
  );

  const map = useMap(mapOptions, mapContainerRef, handleMapReady);

  useEffect(() => {
    if (mode === 'POINT_ADD' && map) {
      const container = map?.current?.ref.getContainer();
      if (container) {
        container.style.cursor = 'crosshair';
      }
      map?.current?.ref?.addOneTimeEventListener('click', (e: any) => {
        const { lat, lng } = e.latlng;
        const coords = { lat, lon: lng };
        if (container) {
          container.style.cursor = '';
        }
        if (onPointAdded) {
          onPointAdded(coords);
        }
      });
    }
  }, [map, mode, onPointAdded]);

  // listen to wrapper div size change to avoid missing tiles
  useEffect(() => {
    if (!actualMapInstance?.ref) {
      return;
    }
    const mapDivWrapper = actualMapInstance?.ref?.getContainer?.();

    if (!mapDivWrapper) {
      return;
    }

    const observer = new ResizeObserver(() => {
      actualMapInstance.ref.invalidateSize();
    });

    observer.observe(mapDivWrapper);

    return () => {
      observer.disconnect();
    };
  }, [actualMapInstance]);

  return (
    <MapContext.Provider
      value={{
        map: { current: actualMapInstance },
        isLoaded: false,
      }}
    >
      {showSuggest && (
        <div className={classes.root}>
          <MapSuggest />
        </div>
      )}
      <div ref={mapContainerRef} id="map" style={{ width, height }}>
        {children}
      </div>
    </MapContext.Provider>
  );
}
